import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { FaPencilAlt, FaEraser, FaTimes, FaTrashAlt, FaUndo, FaRedo, FaEye, FaEyeSlash, FaArrowsAlt, FaTrash } from "react-icons/fa";
import { useUser } from "../../contexts/user";
import { useDrawingSuppressed, usePublishCanvasReserved } from "../../contexts/drawing";
import {
  fetchDrawing,
  saveDrawing,
  clearDrawing,
  drawingKey,
  type DrawingStroke,
} from "../../lib/drawing";

// A site-wide freehand annotation layer, mounted once in root.tsx so it covers
// every page. Everyone sees the owner's saved drawing; only the owner (canDraw)
// gets the toolbar and can draw.
//
// Placement model — a stroke stays on the CONTENT it was drawn over, at every
// width, and is NEVER positioned from the viewport's left edge. When a stroke is
// finished it anchors to a stable, intentional handle (see resolveEl/anchorFor),
// not to a reverse-engineered CSS path of the deepest element: an explicit
// [data-draw-anchor] hook the page places on a content column, or else the Nth
// content column in document order. Each point is stored as a pixel offset from
// that handle's top-left. On render the handle is re-resolved and the stroke
// redrawn relative to its current position, so it tracks the centred content at
// any width. Handles are width-stable by construction — a hook id and a column's
// document order don't change with the viewport — unlike a deep element (a <p>
// the framework may render only when narrow) behind a fragile nth-of-type path.
// A fixed max-width column doesn't reflow internally, so the offset lands on the
// same content everywhere.
//
// Fallbacks, in order, none of which reference the left edge: `ax`/`ay` hold the
// handle's draw-time position and `cx` the draw-time viewport centre; if a handle
// ever fails to resolve, the base is shifted by (currentCentre - cx) so the
// stroke still tracks the centred column. A stroke over empty space (no column
// under it) uses the CENTER_ANCHOR sentinel, positioned from the centre line
// directly. Vertical stays absolute. Legacy strokes stored with anchor ""
// (absolute coordinates from the left edge) are healed to the centre-relative
// form for the owner on load; only pre-`cx` legacy strokes can still fall back
// to a raw absolute position.

// Toolbar palette — mirrors the owner's pen colours.
const COLORS = ["#0F1B2D", "#C8102E", "#2563EB", "#2E9E5B", "#E2662B", "#7C3AED", "#DB2777"];
const DEFAULT_SIZE = 4;
const HIDE_PREFIX = "gz-draw-hidden:";
// Drawings are authored on desktop and element-anchoring can't survive the
// mobile/tablet reflow (stacked sidebars, collapsed columns), so the layer is
// desktop-only: hidden below this width.
const DESKTOP_MIN_WIDTH = 1300;
// Sentinel anchor for strokes drawn over empty space: positioned relative to the
// document's horizontal centre line rather than the left edge, so they track the
// centred content column across viewport widths.
const CENTER_ANCHOR = "@center";

// The document's horizontal centre line. The content column is centred in the
// viewport, so this is also the column's centre. Uses clientWidth (the visible
// width) so a horizontal scrollbar or a stray wide element can't shift it.
function docCenterX(): number {
  return document.documentElement.clientWidth / 2;
}

// Build an SVG path from absolute document-space points. A single point renders
// as a dot; otherwise a round-capped polyline.
function pathFromAbs(points: [number, number][]): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y} l 0.01 0`;
  }
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length; i++) d += ` L ${points[i][0]} ${points[i][1]}`;
  return d;
}

// A stroke anchors to a stable, intentional handle so it resolves to the same
// content at every width and nesting change — never a reverse-engineered CSS
// path. Two handle kinds, stored as a sentinel string:
//   @a:<id>   — an explicit [data-draw-anchor="<id>"] hook the page places on a
//               content column. Survives layout refactors and content edits; the
//               preferred handle.
//   @col:<n>  — the Nth content column in document order (see contentColumns).
//               Needs no markup and is width-stable (column order doesn't change
//               with width); only shifts if columns are added/removed/reordered.
// Legacy strokes stored a raw CSS path (main > div > section:nth-of-type(1) …);
// resolveEl still resolves those, but new strokes never create one.
const HOOK_ANCHOR = "@a:";
const COLUMN_ANCHOR = "@col:";
// The de-facto content column across the site (there is no shared component):
// a centred, width-constrained Tailwind "max-w-* mx-auto" box.
const COLUMN_SELECTOR = '[class*="max-w-"][class*="mx-auto"]';

// The page's content columns, outermost only (a column nested inside another is
// not listed), in document order. Capture and render use the same list, so an
// index denotes the same box on every client and width.
function contentColumns(): Element[] {
  const all = Array.from(document.querySelectorAll(COLUMN_SELECTOR));
  return all.filter((el) => !all.some((other) => other !== el && other.contains(el)));
}

// The outermost content column containing `el`, or null — matches an entry of
// contentColumns() so its index is meaningful.
function outermostColumn(el: Element): Element | null {
  let node: Element | null = el;
  let found: Element | null = null;
  while (node && node !== document.body) {
    if (node.matches(COLUMN_SELECTOR)) found = node;
    node = node.parentElement;
  }
  return found;
}

function resolveEl(anchor: string): Element | null {
  if (!anchor) return null;
  try {
    if (anchor.startsWith(HOOK_ANCHOR))
      return document.querySelector(`[data-draw-anchor="${CSS.escape(anchor.slice(HOOK_ANCHOR.length))}"]`);
    if (anchor.startsWith(COLUMN_ANCHOR)) return contentColumns()[Number(anchor.slice(COLUMN_ANCHOR.length))] ?? null;
    if (anchor === CENTER_ANCHOR) return null; // positioned by anchorBase, not an element
    if (anchor.startsWith("#")) return document.querySelector(anchor);
    return document.body.querySelector(`:scope > ${anchor}`); // legacy CSS path
  } catch {
    return null;
  }
}

// The anchor element's current document-space top-left, or a fallback when it
// can't be resolved.
function anchorBase(stroke: Pick<DrawingStroke, "anchor" | "ax" | "ay" | "cx">): [number, number] {
  const el = resolveEl(stroke.anchor);
  if (el) {
    const r = el.getBoundingClientRect();
    return [r.left + window.scrollX, r.top + window.scrollY];
  }
  // Empty-space stroke: re-resolve the current centre line so it tracks the
  // centred content instead of the viewport left. Vertical stays absolute (ay).
  if (stroke.anchor === CENTER_ANCHOR) return [docCenterX(), stroke.ay];
  // Element anchor failed to resolve at this width (responsive DOM differs from
  // draw time). Shift the absolute fallback by the change in the centre line so
  // the stroke tracks the centred content column rather than pinning to the left
  // edge. Legacy strokes lack `cx` and fall back to the raw absolute position.
  if (typeof stroke.cx === "number") return [stroke.ax + (docCenterX() - stroke.cx), stroke.ay];
  return [stroke.ax, stroke.ay];
}

// Re-express a legacy empty-space stroke (anchor "", absolute document
// coordinates from the left edge) as a CENTER_ANCHOR stroke using the current
// centre line. Exact when the owner is at the width the stroke was drawn at.
function healLegacyStroke(stroke: DrawingStroke): DrawingStroke {
  if (stroke.anchor !== "") return stroke;
  const cx = docCenterX();
  return {
    ...stroke,
    anchor: CENTER_ANCHOR,
    ax: cx,
    ay: 0,
    points: stroke.points.map(([dx, dy]) => [stroke.ax + dx - cx, stroke.ay + dy] as [number, number]),
  };
}

// Keep only strokes in the current (element-anchored) shape. Drops anything from
// an older model so mixed data never renders broken or blocks a save.
function isValidStroke(s: unknown): s is DrawingStroke {
  const st = s as Partial<DrawingStroke>;
  return (
    !!st &&
    typeof st.anchor === "string" &&
    Number.isFinite(st.ax) &&
    Number.isFinite(st.ay) &&
    Array.isArray(st.points) &&
    st.points.length > 0
  );
}

function measureDocWidth(): number {
  return Math.max(document.documentElement.clientWidth, document.documentElement.scrollWidth);
}
function measureDocHeight(): number {
  return Math.max(
    document.body?.scrollHeight ?? 0,
    document.documentElement.scrollHeight,
    document.documentElement.clientHeight,
  );
}

export default function DrawingLayer() {
  const location = useLocation();
  const path = drawingKey(location.pathname);
  const { canDraw } = useUser();
  const suppressed = useDrawingSuppressed();

  const [mounted, setMounted] = useState(false);
  const [strokes, setStrokes] = useState<DrawingStroke[]>([]);
  const [hidden, setHidden] = useState(false);

  // Document size (sizes the capture surface), viewport width (drives the
  // desktop-only gate), and a tick bumped on any layout change so anchored
  // strokes re-resolve their positions.
  const [docW, setDocW] = useState(0);
  const [docH, setDocH] = useState(0);
  const [vw, setVw] = useState(0);
  const [layoutTick, setLayoutTick] = useState(0);

  // Owner-only editing state. `tool` is null when neither pen nor eraser is
  // selected — the overlay then lets pointer events through so the owner can
  // scroll and click the page with the toolbar still open.
  const [editing, setEditing] = useState(false);
  const [tool, setTool] = useState<"pen" | "eraser" | "select" | null>("pen");
  const [color, setColor] = useState(COLORS[0]);
  // Select tool: index of the currently-selected stroke (null = none). The drag
  // ref snapshots the stroke's points at grab time so a move is computed from the
  // grab point each frame (no cumulative drift), and `moved` gates the save so a
  // plain click-to-select doesn't mark the drawing dirty.
  const [selected, setSelected] = useState<number | null>(null);
  const dragRef = useRef<{ index: number; origin: [number, number]; points: [number, number][]; moved: boolean } | null>(null);
  const size = DEFAULT_SIZE;
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redo, setRedo] = useState<DrawingStroke[]>([]);

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const drawingRef = useRef(false);
  // The in-progress stroke, in absolute document coordinates (anchored on finish).
  // Held in a ref (source of truth for the geometry) and mirrored to state for
  // the live preview render.
  const livePointsRef = useRef<[number, number][] | null>(null);
  const [live, setLive] = useState<[number, number][] | null>(null);

  const remeasure = useCallback(() => {
    // Measure the document's true extent with the overlay collapsed. The overlay
    // is a full-document box pinned at the top-left, so its own width/height feed
    // back into scrollWidth/scrollHeight; if we sized it from a scrollWidth that
    // already included the overlay, any one-frame overshoot (a stale size carried
    // across an SPA navigation, a load-time transient, a scrollbar quirk) would
    // latch permanently — measureDoc* takes a Math.max that never shrinks — and
    // pin a phantom horizontal scrollbar. Collapsing it first breaks that loop
    // while still letting the layer grow to cover legitimately wider content.
    const overlay = overlayRef.current;
    if (overlay) {
      overlay.style.width = "0px";
      overlay.style.height = "0px";
    }
    const w = measureDocWidth();
    const h = measureDocHeight();
    if (overlay) {
      // Restore straight to the freshly measured size so there is no frame where
      // the old (possibly larger) size re-inflates the document mid-measure.
      overlay.style.width = `${w}px`;
      overlay.style.height = `${h}px`;
    }
    setDocW(w);
    setDocH(h);
    setVw(document.documentElement.clientWidth);
    setLayoutTick((t) => t + 1);
  }, []);

  useEffect(() => {
    setMounted(true);
    setDocW(measureDocWidth());
    setDocH(measureDocHeight());
    setVw(document.documentElement.clientWidth);
  }, []);

  // Re-resolve anchors whenever the layout changes (resize, font/image load,
  // content height shifts). rAF-debounced so a burst collapses to one update.
  useEffect(() => {
    let scheduled = false;
    const onChange = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        remeasure();
      });
    };
    const ro = new ResizeObserver(onChange);
    ro.observe(document.body);
    window.addEventListener("resize", onChange);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onChange);
    };
  }, [remeasure]);

  // (Re)load whenever the page changes. Editing is reset so the owner never
  // carries an unsaved drawing across navigation.
  useEffect(() => {
    let alive = true;
    setEditing(false);
    setDirty(false);
    setRedo([]);
    setLive(null);
    setHidden(typeof window !== "undefined" && window.localStorage.getItem(HIDE_PREFIX + path) === "1");
    fetchDrawing(path).then((d) => {
      if (!alive) return;
      const loaded = (d?.data.strokes ?? []).filter(isValidStroke);
      // One-time migration: legacy empty-space strokes were pinned to the
      // viewport left. Re-anchor them to the centre line so they track content.
      // Owner-only (needs the current width to be the draw width, and only the
      // owner can persist), and it re-saves once so every viewer gets the fix.
      const needsHeal = canDraw && loaded.some((s) => s.anchor === "");
      const healed = needsHeal ? loaded.map(healLegacyStroke) : loaded;
      setStrokes(healed);
      if (needsHeal) {
        saveDrawing(path, { strokes: healed.filter(isValidStroke) }).catch(() => {
          /* non-fatal; the heal still applies in-memory and retries next load */
        });
      }
      requestAnimationFrame(() => {
        if (alive) remeasure();
      });
    });
    // Re-resolve anchors after late layout shifts (web-font swap, images that
    // arrive without a reserved box). The ResizeObserver catches most; these are
    // cheap insurance for the first couple of seconds.
    const timers = [400, 1200, 2500].map((ms) => window.setTimeout(() => alive && remeasure(), ms));
    return () => {
      alive = false;
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [path, remeasure, canDraw]);

  // Auto-save: a couple of seconds after the last change, persist silently. The
  // manual Save button still exists and surfaces errors; this just means the
  // owner rarely has to think about saving. Each new stroke resets the timer.
  useEffect(() => {
    if (!editing || !dirty || saving) return;
    const t = window.setTimeout(() => {
      saveDrawing(path, { strokes: strokes.filter(isValidStroke) })
        .then(() => setDirty(false))
        .catch(() => {
          /* keep dirty; the manual Save button surfaces the error on demand */
        });
    }, 2500);
    return () => window.clearTimeout(t);
  }, [editing, dirty, saving, strokes, path]);

  // Safety net: warn before leaving/closing the tab with unsaved ink.
  useEffect(() => {
    if (!(editing && dirty)) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [editing, dirty]);

  // Pointer → absolute document coordinates.
  const toDoc = (clientX: number, clientY: number): [number, number] => [
    clientX + window.scrollX,
    clientY + window.scrollY,
  ];

  const eraseAt = useCallback((docX: number, docY: number) => {
    const radius = 14;
    setStrokes((prev) => {
      const next = prev.filter((st) => {
        const [bx, by] = anchorBase(st);
        return !st.points.some(([dx, dy]) => Math.hypot(bx + dx - docX, by + dy - docY) <= radius + st.size / 2);
      });
      if (next.length !== prev.length) setDirty(true);
      return next;
    });
  }, []);

  // Topmost stroke (last drawn wins) whose ink passes within `radius` of the
  // point, or null. Same distance test the eraser uses, so what you can rub out
  // is what you can select.
  const hitStrokeIndex = useCallback(
    (docX: number, docY: number): number | null => {
      const radius = 12;
      for (let i = strokes.length - 1; i >= 0; i--) {
        const st = strokes[i];
        const [bx, by] = anchorBase(st);
        if (st.points.some(([dx, dy]) => Math.hypot(bx + dx - docX, by + dy - docY) <= radius + st.size / 2)) return i;
      }
      return null;
    },
    [strokes],
  );

  const deleteSelected = useCallback(() => {
    if (selected === null) return;
    setStrokes((prev) => prev.filter((_, i) => i !== selected));
    setSelected(null);
    setDirty(true);
  }, [selected]);

  // Leaving the select tool drops the selection (so the highlight/handles clear).
  useEffect(() => {
    if (tool !== "select") setSelected(null);
  }, [tool]);

  // Keyboard for the select tool: Delete/Backspace removes the selected line,
  // the arrow keys nudge it (Shift = a bigger step), Escape deselects. Only bound
  // while a line is actually selected so it never swallows other page shortcuts.
  useEffect(() => {
    if (!editing || tool !== "select" || selected === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelected();
        return;
      }
      if (e.key === "Escape") {
        setSelected(null);
        return;
      }
      const step = e.shiftKey ? 16 : 3;
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else return;
      e.preventDefault();
      setStrokes((prev) =>
        prev.map((st, i) =>
          i === selected ? { ...st, points: st.points.map(([px, py]) => [px + dx, py + dy] as [number, number]) } : st,
        ),
      );
      setDirty(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, tool, selected, deleteSelected]);

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!editing || !tool) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const pt = toDoc(e.clientX, e.clientY);
    drawingRef.current = true;
    if (tool === "eraser") {
      eraseAt(pt[0], pt[1]);
      return;
    }
    if (tool === "select") {
      const hit = hitStrokeIndex(pt[0], pt[1]);
      setSelected(hit);
      dragRef.current = hit === null ? null : { index: hit, origin: pt, points: strokes[hit].points, moved: false };
      return;
    }
    setRedo([]);
    livePointsRef.current = [pt];
    setLive([pt]);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!editing || !drawingRef.current) return;
    const pt = toDoc(e.clientX, e.clientY);
    if (tool === "eraser") {
      eraseAt(pt[0], pt[1]);
      return;
    }
    if (tool === "select") {
      const d = dragRef.current;
      if (!d) return;
      const dx = pt[0] - d.origin[0];
      const dy = pt[1] - d.origin[1];
      if (!d.moved && Math.hypot(dx, dy) < 2) return; // ignore sub-pixel jitter on a click-to-select
      d.moved = true;
      setStrokes((prev) =>
        prev.map((st, i) =>
          i === d.index ? { ...st, points: d.points.map(([px, py]) => [px + dx, py + dy] as [number, number]) } : st,
        ),
      );
      return;
    }
    const cur = livePointsRef.current ?? [];
    const last = cur[cur.length - 1];
    if (last && Math.hypot(pt[0] - last[0], pt[1] - last[1]) < 1.5) return; // thin out
    livePointsRef.current = [...cur, pt];
    setLive(livePointsRef.current);
  };

  // Pick what a finished stroke anchors to: an explicit [data-draw-anchor] hook
  // if the content under the stroke's centre has one, else the content column it
  // sits in (overlay hidden so it doesn't hit-test itself). Both resolve to the
  // same content at any width; neither is a reverse-engineered path.
  const anchorFor = (docPoints: [number, number][]): { anchor: string; base: [number, number] } => {
    let sx = 0;
    let sy = 0;
    for (const [x, y] of docPoints) {
      sx += x;
      sy += y;
    }
    const cx = sx / docPoints.length - window.scrollX;
    const cy = sy / docPoints.length - window.scrollY;
    const overlay = overlayRef.current;
    const prev = overlay?.style.pointerEvents;
    if (overlay) overlay.style.pointerEvents = "none";
    const el = document.elementFromPoint(cx, cy);
    if (overlay) overlay.style.pointerEvents = prev ?? "";
    const baseOf = (box: Element): [number, number] => {
      const r = box.getBoundingClientRect();
      return [r.left + window.scrollX, r.top + window.scrollY];
    };
    const hook = el?.closest("[data-draw-anchor]") ?? null;
    const hookId = hook?.getAttribute("data-draw-anchor");
    if (hook && hookId) return { anchor: `${HOOK_ANCHOR}${hookId}`, base: baseOf(hook) };
    const col = el ? outermostColumn(el) : null;
    if (col) {
      const idx = contentColumns().indexOf(col);
      if (idx >= 0) return { anchor: `${COLUMN_ANCHOR}${idx}`, base: baseOf(col) };
    }
    // No column under the stroke — anchor to the centre line so it tracks the
    // centred content. Base y is 0 (points carry absolute y).
    return { anchor: CENTER_ANCHOR, base: [docCenterX(), 0] };
  };

  const finishStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (tool === "select") {
      if (dragRef.current?.moved) setDirty(true);
      dragRef.current = null;
      return;
    }
    const pts = livePointsRef.current;
    livePointsRef.current = null;
    if (pts && pts.length > 0 && tool === "pen") {
      const { anchor, base } = anchorFor(pts);
      const points = pts.map(([x, y]) => [x - base[0], y - base[1]] as [number, number]);
      setStrokes((s) => [
        ...s,
        { tool: "pen", color, size, anchor, ax: base[0], ay: base[1], cx: docCenterX(), points },
      ]);
      setDirty(true);
    }
    setLive(null);
  };

  const undo = () => {
    setSelected(null);
    setStrokes((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedo((r) => [...r, last]);
      setDirty(true);
      return prev.slice(0, -1);
    });
  };

  const redoStroke = () => {
    setSelected(null);
    setRedo((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setStrokes((s) => [...s, last]);
      setDirty(true);
      return prev.slice(0, -1);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveDrawing(path, { strokes: strokes.filter(isValidStroke) });
      setDirty(false);
    } catch (err) {
      alert((err as Error).message || "Could not save the drawing");
    } finally {
      setSaving(false);
    }
  };

  const deleteAll = async () => {
    if (!confirm("Delete the drawing on this page for everyone?")) return;
    setSaving(true);
    try {
      await clearDrawing(path);
      setStrokes([]);
      setRedo([]);
      setSelected(null);
      setDirty(false);
    } catch (err) {
      alert((err as Error).message || "Could not delete the drawing");
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = () => {
    setHidden((h) => {
      const next = !h;
      try {
        if (next) window.localStorage.setItem(HIDE_PREFIX + path, "1");
        else window.localStorage.removeItem(HIDE_PREFIX + path);
      } catch {
        /* localStorage can throw in private mode — the in-memory toggle still works */
      }
      return next;
    });
  };

  const hasDrawing = strokes.length > 0;
  const showOverlay = docW > 0 && ((hasDrawing && !hidden) || editing);
  // Tell the page whether it should reserve blank space for the canvas: while
  // the toolbar is open, or when this page already carries ink that was drawn
  // over that space (see useCanvasReserved).
  usePublishCanvasReserved(editing || hasDrawing);
  // Capture input only while a tool is active; otherwise let clicks/scroll reach
  // the page even with the toolbar open.
  const capturing = editing && tool !== null;

  // Resolve every stroke against the current layout (re-runs on layoutTick).
  const renderedStrokes = useMemo(() => {
    void layoutTick; // dependency: positions change when layout does
    const list = strokes.map((st, i) => {
      const [bx, by] = anchorBase(st);
      const abs = st.points.map(([dx, dy]) => [bx + dx, by + dy] as [number, number]);
      if (i !== selected) {
        return (
          <path
            key={i}
            d={pathFromAbs(abs)}
            fill="none"
            stroke={st.color}
            strokeWidth={st.size}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      }
      // Selected (select tool): a translucent halo under the ink plus a dashed
      // bounding box, so the owner can see exactly which line will move/delete.
      const xs = abs.map((p) => p[0]);
      const ys = abs.map((p) => p[1]);
      const pad = 8 + st.size;
      const minX = Math.min(...xs) - pad;
      const minY = Math.min(...ys) - pad;
      const w = Math.max(...xs) - Math.min(...xs) + pad * 2;
      const h = Math.max(...ys) - Math.min(...ys) + pad * 2;
      return (
        <g key={i}>
          <path
            d={pathFromAbs(abs)}
            fill="none"
            stroke="#2563EB"
            strokeWidth={st.size + 8}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.25}
          />
          <path
            d={pathFromAbs(abs)}
            fill="none"
            stroke={st.color}
            strokeWidth={st.size}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <rect
            x={minX}
            y={minY}
            width={w}
            height={h}
            fill="none"
            stroke="#2563EB"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            rx={6}
          />
        </g>
      );
    });
    if (live && live.length > 0) {
      list.push(
        <path
          key="live"
          d={pathFromAbs(live)}
          fill="none"
          stroke={color}
          strokeWidth={size}
          strokeLinecap="round"
          strokeLinejoin="round"
        />,
      );
    }
    return list;
  }, [strokes, live, color, size, layoutTick, selected]);

  if (!mounted) return null;
  // Suppressed by the current page (e.g. a locked lesson showing a paywall
  // instead of its content) — never render ink or the toolbar there.
  if (suppressed) return null;
  // Desktop-only: below the breakpoint, render nothing (no ink, no notes toggle,
  // no Draw toolbar). Reactive — resizing across the breakpoint shows/hides it.
  if (vw > 0 && vw < DESKTOP_MIN_WIDTH) return null;

  const overlay = showOverlay ? (
    <div
      ref={overlayRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: `${docW}px`,
        height: `${docH}px`,
        zIndex: 40,
        pointerEvents: capturing ? "auto" : "none",
        userSelect: capturing ? "none" : "auto",
        WebkitUserSelect: capturing ? "none" : "auto",
      }}
    >
      <svg
        width={docW}
        height={docH}
        style={{
          display: "block",
          overflow: "visible",
          cursor: capturing ? (tool === "select" ? "move" : "crosshair") : "default",
          touchAction: capturing ? "none" : "auto",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
        onPointerCancel={finishStroke}
      >
        {renderedStrokes}
      </svg>
    </div>
  ) : null;

  return createPortal(
    <>
      {overlay}
      <DrawingControls
        canDraw={canDraw}
        hasDrawing={hasDrawing}
        hidden={hidden}
        editing={editing}
        tool={tool}
        color={color}
        colors={COLORS}
        dirty={dirty}
        saving={saving}
        canUndo={strokes.length > 0}
        canRedo={redo.length > 0}
        selectedLine={selected !== null}
        onDeleteSelected={deleteSelected}
        onToggleHidden={toggleHidden}
        onStartEdit={() => {
          remeasure();
          setEditing(true);
        }}
        onStopEdit={() => {
          finishStroke();
          // Flush any unsaved ink on the way out.
          if (dirty) {
            saveDrawing(path, { strokes: strokes.filter(isValidStroke) })
              .then(() => setDirty(false))
              .catch(() => {});
          }
          setEditing(false);
        }}
        // Toggle a tool: clicking the active tool deselects it (overlay becomes
        // click-through), clicking an inactive one selects it.
        onPickTool={(t) => setTool((cur) => (cur === t ? null : t))}
        onPickColor={(c) => {
          setColor(c);
          setTool("pen");
        }}
        onUndo={undo}
        onRedo={redoStroke}
        onSave={save}
        onDelete={deleteAll}
      />
    </>,
    document.body,
  );
}

interface ControlsProps {
  canDraw: boolean;
  hasDrawing: boolean;
  hidden: boolean;
  editing: boolean;
  tool: "pen" | "eraser" | "select" | null;
  color: string;
  colors: string[];
  dirty: boolean;
  saving: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selectedLine: boolean;
  onDeleteSelected: () => void;
  onToggleHidden: () => void;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onPickTool: (t: "pen" | "eraser" | "select") => void;
  onPickColor: (c: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onDelete: () => void;
}

function DrawingControls(props: ControlsProps) {
  const { canDraw, hasDrawing, hidden, editing } = props;

  return (
    <>
      {/* Visitor (and idle-owner) control: hide/show the owner's notes per page.
          Only shown when a drawing exists and nobody is actively editing. */}
      {hasDrawing && !editing && (
        <button
          type="button"
          onClick={props.onToggleHidden}
          aria-pressed={hidden}
          style={fabStyle("left")}
          title={hidden ? "Show the owner's annotations on this page" : "Hide the annotations on this page"}
        >
          {hidden ? <FaEye aria-hidden /> : <FaEyeSlash aria-hidden />}
          <span style={{ fontSize: 13 }}>{hidden ? "Show notes" : "Hide notes"}</span>
        </button>
      )}

      {/* Owner launcher: opens the drawing toolbar. */}
      {canDraw && !editing && (
        <button
          type="button"
          onClick={props.onStartEdit}
          style={fabStyle("right")}
          title="Draw on this page"
        >
          <FaPencilAlt aria-hidden />
          <span style={{ fontSize: 13 }}>Draw</span>
        </button>
      )}

      {/* Owner toolbar. */}
      {canDraw && editing && (
        <div style={toolbarStyle}>
          <IconButton active={props.tool === "pen"} onClick={() => props.onPickTool("pen")} title="Pen">
            <FaPencilAlt aria-hidden />
          </IconButton>

          {props.colors.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => props.onPickColor(c)}
              title={`Pen colour ${c}`}
              aria-label={`Pen colour ${c}`}
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: c,
                border: props.color === c && props.tool === "pen" ? "3px solid #0F1B2D" : "3px solid #E5E7EB",
                boxShadow: props.color === c && props.tool === "pen" ? "0 0 0 2px #fff inset" : "none",
                cursor: "pointer",
                flex: "0 0 auto",
              }}
            />
          ))}

          <IconButton active={props.tool === "eraser"} onClick={() => props.onPickTool("eraser")} title="Eraser">
            <FaEraser aria-hidden />
          </IconButton>

          <IconButton
            active={props.tool === "select"}
            onClick={() => props.onPickTool("select")}
            title="Select a line — click to pick, drag to move, arrows to nudge"
          >
            <FaArrowsAlt aria-hidden />
          </IconButton>

          <IconButton
            disabled={!props.selectedLine}
            onClick={props.onDeleteSelected}
            title="Delete the selected line (Del)"
          >
            <FaTrash aria-hidden />
          </IconButton>

          <span style={{ width: 1, alignSelf: "stretch", background: "#E5E7EB", margin: "0 2px" }} />

          <IconButton disabled={!props.canUndo} onClick={props.onUndo} title="Undo">
            <FaUndo aria-hidden />
          </IconButton>
          <IconButton disabled={!props.canRedo} onClick={props.onRedo} title="Redo">
            <FaRedo aria-hidden />
          </IconButton>
          <IconButton onClick={props.onDelete} title="Delete the whole drawing for everyone">
            <FaTrashAlt aria-hidden />
          </IconButton>

          <button
            type="button"
            onClick={props.onSave}
            disabled={props.saving || !props.dirty}
            style={{
              marginLeft: 4,
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: props.dirty ? "#0F1B2D" : "#9CA3AF",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: props.saving || !props.dirty ? "default" : "pointer",
            }}
          >
            {props.saving ? "Saving…" : props.dirty ? "Save" : "Saved"}
          </button>

          <IconButton onClick={props.onStopEdit} title="Close toolbar">
            <FaTimes aria-hidden />
          </IconButton>
        </div>
      )}
    </>
  );
}

function IconButton({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      style={{
        width: 38,
        height: 38,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 8,
        border: active ? "2px solid #2563EB" : "2px solid transparent",
        background: active ? "#EFF6FF" : "transparent",
        color: disabled ? "#C4C9D2" : "#0F1B2D",
        cursor: disabled ? "default" : "pointer",
        flex: "0 0 auto",
        fontSize: 15,
      }}
    >
      {children}
    </button>
  );
}

function fabStyle(side: "left" | "right"): React.CSSProperties {
  return {
    position: "fixed",
    bottom: 20,
    [side]: 20,
    zIndex: 50,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    borderRadius: 999,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#0F1B2D",
    boxShadow: "0 4px 16px rgba(9,36,75,0.15)",
    cursor: "pointer",
    fontWeight: 600,
  };
}

const toolbarStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 50,
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 10px",
  borderRadius: 14,
  border: "1px solid #E5E7EB",
  background: "#fff",
  boxShadow: "0 6px 24px rgba(9,36,75,0.18)",
  maxWidth: "calc(100vw - 24px)",
  flexWrap: "wrap",
  justifyContent: "center",
};
